import { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Brain,
  Database,
  Loader2,
  RefreshCw,
  Shield,
  ExternalLink,
  BookMarked,
  FolderOpen,
  Sparkles,
  Trash2,
  Play,
} from 'lucide-react';
import { PermissionProvider, usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppHeader } from '../contexts/AppHeaderContext';
import api from '../utils/axiosConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { cn } from '../lib/utils';
import AppPageShell from '../components/layout/AppPageShell';

/** Wait for full agentic run; must be ≤ setupProxy gap path proxyTimeout (margin kept on proxy). */
const GAP_REQUEST_MS = 600000;
const LINK_METADATA_TIMEOUT_MS = 45000;
const LINK_SAVE_TIMEOUT_MS = 120000;

function CoachingSkillsAdminContent() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const reduceMotion = useReducedMotion();
  const { setHeaderBackButton, setHeaderCenter } = useAppHeader();
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [reindexDialogOpen, setReindexDialogOpen] = useState(false);
  const [message, setMessage] = useState(null);

  const [platformItems, setPlatformItems] = useState([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [linkPreviewLoading, setLinkPreviewLoading] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkPreviewError, setLinkPreviewError] = useState(null);
  const linkMetaSeq = useRef(0);
  const [uploading, setUploading] = useState(false);

  const [actionItems, setActionItems] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('open');
  const [manualTitle, setManualTitle] = useState('');
  const [manualText, setManualText] = useState('');
  const [manualSlug, setManualSlug] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const [gapRunning, setGapRunning] = useState(false);
  const [gapRun, setGapRun] = useState(null);
  const [gapRecs, setGapRecs] = useState([]);
  const [syncingCoachingQueue, setSyncingCoachingQueue] = useState(false);

  const load = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) {
      setLoading(true);
      setMessage(null);
    }
    try {
      const [ov, src, nd] = await Promise.all([
        api.get('/api/coach-skills/overview-stats'),
        api.get('/api/coach-skills/sources'),
        api.get('/api/coach-skills/nodes', { params: { limit: 40 } }),
      ]);
      setOverview(ov.data);
      setSources(src.data?.sources || []);
      setNodes(nd.data?.nodes || []);
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  const loadPlatform = useCallback(async () => {
    setPlatformLoading(true);
    try {
      const res = await api.get('/api/platform-content', { params: { limit: 100 } });
      setPlatformItems(res.data?.items || []);
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('adminCoaching.libraryLoadFailed'),
      });
    } finally {
      setPlatformLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const raw = linkUrl.trim();
    if (!raw || !/^https?:\/\//i.test(raw)) {
      setLinkPreview(null);
      setLinkPreviewError(null);
      setLinkPreviewLoading(false);
      return;
    }
    const seq = ++linkMetaSeq.current;
    const timer = setTimeout(async () => {
      setLinkPreviewLoading(true);
      setLinkPreviewError(null);
      try {
        const res = await api.get('/api/links/metadata', {
          params: { url: raw },
          timeout: LINK_METADATA_TIMEOUT_MS,
        });
        if (seq !== linkMetaSeq.current) return;
        setLinkPreview({
          title: res.data?.title || null,
          description: res.data?.description || null,
          image_url: res.data?.image_url || null,
        });
        setLinkTitle((prev) => (prev.trim() ? prev : res.data?.title || ''));
      } catch (e) {
        if (seq !== linkMetaSeq.current) return;
        setLinkPreview(null);
        setLinkPreviewError(e.response?.data?.error || t('adminCoaching.libraryPreviewFailed'));
      } finally {
        if (seq === linkMetaSeq.current) setLinkPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [linkUrl, t]);

  const loadActions = useCallback(
    async (filterOverride) => {
      const f = filterOverride != null ? filterOverride : actionFilter;
      setActionLoading(true);
      try {
        let items = [];
        if (f === 'open') {
          const res = await api.get('/api/coach-skills/actions', {
            params: { limit: 80, status: 'open' },
            timeout: 60000,
          });
          items = res.data?.items || [];
        } else {
          const params = { limit: 80 };
          if (f !== 'all') params.status = f;
          const res = await api.get('/api/coach-skills/actions', {
            params,
            timeout: 60000,
          });
          items = res.data?.items || [];
        }
        setActionItems(items);
      } catch (e) {
        setMessage({
          type: 'err',
          text: e.response?.data?.error || t('adminCoaching.actionsLoadFailed'),
        });
      } finally {
        setActionLoading(false);
      }
    },
    [actionFilter, t]
  );

  useEffect(() => {
    setHeaderBackButton(true);
    setHeaderCenter(
      <span className='text-sm font-medium text-foreground truncate flex items-center gap-2'>
        <Shield className='h-4 w-4 text-primary shrink-0' aria-hidden />
        {t('adminCoaching.pageTitle')}
      </span>
    );
    return () => {
      setHeaderBackButton(false);
      setHeaderCenter(null);
    };
  }, [setHeaderBackButton, setHeaderCenter, t]);

  useEffect(() => {
    if (user?.permissions?.includes('coach_knowledge_manage')) load();
  }, [load, user?.permissions]);

  const runReindex = async () => {
    setReindexDialogOpen(false);
    setReindexing(true);
    setMessage(null);
    try {
      const res = await api.post('/api/coach-skills/reindex');
      setMessage({
        type: 'ok',
        text: t('adminCoaching.reindexDone', {
          files: String(res.data?.files ?? 0),
          chunks: String(res.data?.chunks ?? 0),
        }),
      });
      await load({ silent: true });
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('adminCoaching.reindexFailed'),
      });
    } finally {
      setReindexing(false);
    }
  };

  const submitLink = async () => {
    if (!linkUrl.trim()) return;
    setAddingLink(true);
    setMessage(null);
    try {
      await api.post(
        '/api/platform-content/link',
        {
          url: linkUrl.trim(),
          title: linkTitle.trim() || undefined,
        },
        { timeout: LINK_SAVE_TIMEOUT_MS }
      );
      linkMetaSeq.current += 1;
      setLinkUrl('');
      setLinkTitle('');
      setLinkPreview(null);
      setLinkPreviewError(null);
      setMessage({ type: 'ok', text: t('adminCoaching.libraryLinkAdded') });
      await loadPlatform();
      await load({ silent: true });
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || e.message || t('common.error'),
      });
    } finally {
      setAddingLink(false);
    }
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/api/platform-content/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage({ type: 'ok', text: t('adminCoaching.libraryUploadDone') });
      await loadPlatform();
      await load({ silent: true });
    } catch (err) {
      setMessage({
        type: 'err',
        text: err.response?.data?.error || t('common.error'),
      });
    } finally {
      setUploading(false);
    }
  };

  const toggleCoaching = async (row, checked) => {
    try {
      const res = await api.patch(`/api/platform-content/${row.id}`, { is_coaching_skill: checked });
      setPlatformItems((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, is_coaching_skill: checked ? 1 : 0 } : r))
      );
      if (checked && res.data?.ingest_sync?.reopened) {
        setMessage({ type: 'ok', text: t('adminCoaching.coachingQueuedReopened') });
      } else if (checked && res.data?.ingest_sync?.inserted) {
        setMessage({ type: 'ok', text: t('adminCoaching.coachingQueuedNew') });
      } else if (checked && res.data?.ingest_sync && res.data.ingest_sync.inserted === false) {
        setMessage({ type: 'ok', text: t('adminCoaching.coachingQueuedAlready') });
      }
      await load({ silent: true });
      await loadActions();
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    }
  };

  const syncCoachingQueueFromLibrary = async () => {
    setSyncingCoachingQueue(true);
    setMessage(null);
    try {
      const res = await api.post('/api/platform-content/sync-coaching-queue');
      setMessage({
        type: 'ok',
        text: t('adminCoaching.syncCoachingQueueDone', {
          items: String(res.data?.coaching_items ?? 0),
          created: String(res.data?.ingest_actions_created ?? 0),
        }),
      });
      await loadActions();
      await load({ silent: true });
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    } finally {
      setSyncingCoachingQueue(false);
    }
  };

  const deletePlatformRow = async (id) => {
    if (!window.confirm(t('adminCoaching.libraryDeleteConfirm'))) return;
    try {
      await api.delete(`/api/platform-content/${id}`);
      setPlatformItems((prev) => prev.filter((r) => r.id !== id));
      await load({ silent: true });
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    }
  };

  const executeAction = async (id) => {
    setMessage(null);
    try {
      const res = await api.post(`/api/coach-skills/actions/${id}/execute`);
      setMessage({
        type: 'ok',
        text: t('adminCoaching.actionExecuted', { chunks: String(res.data?.chunks ?? 0) }),
      });
      await loadActions();
      await load({ silent: true });
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    }
  };

  const dismissAction = async (id) => {
    try {
      await api.post(`/api/coach-skills/actions/${id}/dismiss`);
      await loadActions();
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    }
  };

  const submitManualAction = async () => {
    if (!manualTitle.trim() || !manualText.trim()) return;
    setManualSubmitting(true);
    setMessage(null);
    try {
      await api.post('/api/coach-skills/actions/manual', {
        title: manualTitle.trim(),
        ingest_text: manualText.trim(),
        target_topic_slug: manualSlug.trim() || undefined,
      });
      setManualTitle('');
      setManualText('');
      setManualSlug('');
      setMessage({ type: 'ok', text: t('adminCoaching.manualActionCreated') });
      await loadActions();
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    } finally {
      setManualSubmitting(false);
    }
  };

  const runGapAnalysis = async () => {
    setGapRunning(true);
    setMessage(null);
    setGapRun(null);
    setGapRecs([]);
    try {
      const res = await api.post('/api/coach-skills/gap-analysis/run', {}, { timeout: GAP_REQUEST_MS });
      const runId = res.data?.run_id;
      setMessage({
        type: 'ok',
        text: t('adminCoaching.gapDone', { n: String(res.data?.recommendations_inserted ?? 0) }),
      });
      if (runId) {
        const runRes = await api.get(`/api/coach-skills/gap-analysis/runs/${runId}`);
        setGapRun(runRes.data);
        const recRes = await api.get(`/api/coach-skills/gap-analysis/runs/${runId}/recommendations`);
        setGapRecs(recRes.data?.recommendations || []);
      }
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || e.message || t('adminCoaching.gapFailed'),
      });
    } finally {
      setGapRunning(false);
    }
  };

  const promoteRec = async (id, target) => {
    setMessage(null);
    try {
      await api.post(`/api/coach-skills/gap-analysis/recommendations/${id}/promote`, { target });
      setMessage({ type: 'ok', text: t('adminCoaching.gapPromoted') });
      setGapRecs((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'accepted' } : r)));
      await loadActions();
      await load({ silent: true });
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    }
  };

  const dismissRec = async (id) => {
    try {
      await api.post(`/api/coach-skills/gap-analysis/recommendations/${id}/dismiss`);
      setGapRecs((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setMessage({
        type: 'err',
        text: e.response?.data?.error || t('common.error'),
      });
    }
  };

  if (!hasPermission('coach_knowledge_manage')) {
    return <Navigate to='/dashboard' replace />;
  }

  return (
    <AppPageShell>
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className='space-y-2'
      >
        <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold tracking-tight text-foreground flex items-center gap-2'>
              <Brain className='h-8 w-8 text-primary' aria-hidden />
              {t('adminCoaching.pageHeading')}
            </h1>
            <p className='text-muted-foreground mt-1 max-w-2xl'>{t('adminCoaching.pageLead')}</p>
          </div>
          <div className='flex flex-wrap gap-2 shrink-0'>
            {hasPermission('periodization_catalog_manage') ? (
              <Button variant='outline' asChild>
                <Link to='/admin/periodization-catalog'>{t('adminCoaching.openPeriodizationCatalog')}</Link>
              </Button>
            ) : null}
            <Button variant='outline' asChild>
              <Link to='/dashboard'>{t('adminCoaching.backToDashboard')}</Link>
            </Button>
          </div>
        </div>
      </motion.div>

      {message && (
        <Alert variant={message.type === 'err' ? 'destructive' : 'default'}>
          <AlertTitle>{message.type === 'err' ? t('common.error') : t('common.success')}</AlertTitle>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className='flex justify-center py-20 gap-2 text-muted-foreground'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
          <span>{t('common.loading')}</span>
        </div>
      ) : (
        <Tabs
          defaultValue='overview'
          className='space-y-4'
          onValueChange={(v) => {
            if (v === 'library') loadPlatform();
            if (v === 'actions') loadActions();
          }}
        >
          <TabsList className='flex flex-wrap h-auto gap-1 bg-muted/60 p-1'>
            <TabsTrigger value='overview' className='gap-1.5'>
              <Database className='h-3.5 w-3.5' />
              {t('adminCoaching.tabOverview')}
            </TabsTrigger>
            <TabsTrigger value='sources' className='gap-1.5'>
              <BookMarked className='h-3.5 w-3.5' />
              {t('adminCoaching.tabSources')}
            </TabsTrigger>
            <TabsTrigger value='graph' className='gap-1.5'>
              {t('adminCoaching.tabGraph')}
            </TabsTrigger>
            <TabsTrigger value='library' className='gap-1.5'>
              <FolderOpen className='h-3.5 w-3.5' />
              {t('adminCoaching.tabLibrary')}
            </TabsTrigger>
            <TabsTrigger value='actions' className='gap-1.5'>
              {t('adminCoaching.tabActions')}
            </TabsTrigger>
            <TabsTrigger value='gap' className='gap-1.5'>
              <Sparkles className='h-3.5 w-3.5' />
              {t('adminCoaching.tabGap')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value='overview' className='space-y-4'>
            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
              {[
                { label: t('adminCoaching.metricChunks'), value: overview?.chunk_count ?? '—', icon: Brain },
                { label: t('adminCoaching.metricSources'), value: overview?.source_count ?? '—', icon: BookMarked },
                { label: t('adminCoaching.metricNodes'), value: overview?.node_count ?? '—', icon: Database },
                { label: t('adminCoaching.metricEdges'), value: overview?.edge_count ?? '—', icon: Shield },
              ].map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: reduceMotion ? 0 : i * 0.05 }}
                >
                  <Card className='border-border/80 shadow-sm overflow-hidden'>
                    <CardHeader className='pb-2 flex flex-row items-center justify-between space-y-0'>
                      <CardTitle className='text-sm font-medium text-muted-foreground'>{m.label}</CardTitle>
                      <m.icon className='h-4 w-4 text-primary' />
                    </CardHeader>
                    <CardContent>
                      <p className='text-3xl font-bold tabular-nums text-foreground'>{m.value}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>{t('adminCoaching.repositoryCardTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.repositoryCardDesc')}</CardDescription>
              </CardHeader>
              <CardContent className='flex flex-wrap gap-6'>
                <div className='flex items-center gap-3'>
                  <Badge variant='secondary' className='text-base px-3 py-1'>
                    {overview?.documents_count ?? 0}
                  </Badge>
                  <span className='text-sm text-muted-foreground'>{t('adminCoaching.metricDocuments')}</span>
                </div>
                <Separator orientation='vertical' className='hidden sm:block h-10' />
                <div className='flex items-center gap-3'>
                  <Badge variant='secondary' className='text-base px-3 py-1'>
                    {overview?.tracked_links_count ?? 0}
                  </Badge>
                  <span className='text-sm text-muted-foreground'>{t('adminCoaching.metricLinks')}</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='sources'>
            <Card>
              <CardHeader>
                <CardTitle>{t('adminCoaching.sourcesTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.sourcesHint')}</CardDescription>
              </CardHeader>
              <CardContent className='overflow-x-auto'>
                <div className='min-w-[640px] rounded-md border border-border'>
                  <div
                    className='grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border bg-muted/40'
                    role='row'
                  >
                    <div>{t('adminCoaching.colSourceKey')}</div>
                    <div>{t('adminCoaching.colDisplayName')}</div>
                    <div>{t('adminCoaching.colReference')}</div>
                    <div className='text-right'>{t('adminCoaching.colNote')}</div>
                  </div>
                  {sources.length === 0 ? (
                    <div className='px-3 py-8 text-center text-sm text-muted-foreground'>{t('common.noData')}</div>
                  ) : (
                    sources.map((s) => (
                      <div
                        key={s.id}
                        className='grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-2 px-3 py-2.5 text-sm border-b border-border/80 last:border-0 items-center'
                        role='row'
                      >
                        <div className='font-mono text-xs break-all'>{s.source_key}</div>
                        <div className='text-foreground'>{s.display_name}</div>
                        <div>
                          {s.reference_url ? (
                            <a
                              href={s.reference_url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-primary inline-flex items-center gap-1 hover:underline text-xs'
                            >
                              {t('adminCoaching.openLink')}
                              <ExternalLink className='h-3 w-3' />
                            </a>
                          ) : (
                            '—'
                          )}
                        </div>
                        <div className='text-right text-muted-foreground text-xs truncate' title={s.ingestion_note || ''}>
                          {s.ingestion_note || '—'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='graph'>
            <Card>
              <CardHeader>
                <CardTitle>{t('adminCoaching.graphTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.graphHint')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-2'>
                {nodes.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('adminCoaching.graphEmpty')}</p>
                ) : (
                  <ul className='grid gap-2 sm:grid-cols-2'>
                    {nodes.map((n) => (
                      <li
                        key={n.id}
                        className='rounded-lg border border-border bg-card/50 px-3 py-2 text-sm'
                      >
                        <span className='font-medium text-foreground'>{n.label}</span>
                        <span className='text-muted-foreground text-xs ml-2'>({n.slug})</span>
                        {n.node_type && (
                          <Badge variant='outline' className='ml-2 text-[10px]'>
                            {n.node_type}
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='library' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>{t('adminCoaching.libraryTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.libraryDesc')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                <div className='grid gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='pc-link-url'>{t('adminCoaching.libraryUrl')}</Label>
                    <Input
                      id='pc-link-url'
                      type='url'
                      placeholder='https://…'
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                    />
                    <p className='text-xs text-muted-foreground'>{t('adminCoaching.libraryUrlHint')}</p>
                    {linkPreviewLoading && (
                      <div className='flex items-center gap-2 text-sm text-muted-foreground py-2'>
                        <Loader2 className='h-4 w-4 animate-spin text-primary' aria-hidden />
                        {t('adminCoaching.libraryPreviewLoading')}
                      </div>
                    )}
                    {linkPreviewError && (
                      <p className='text-sm text-destructive' role='alert'>
                        {linkPreviewError}
                      </p>
                    )}
                    {!!linkPreview && !linkPreviewLoading && (
                      <div className='rounded-lg border border-border bg-muted/30 p-3 space-y-2 mt-2'>
                        <p className='text-xs font-medium text-muted-foreground'>
                          {t('adminCoaching.libraryPreviewHint')}
                        </p>
                        <div className='flex gap-3'>
                          {linkPreview.image_url ? (
                            <img
                              src={linkPreview.image_url}
                              alt=''
                              className='w-20 h-20 rounded-md object-cover bg-background border border-border shrink-0'
                            />
                          ) : null}
                          <div className='min-w-0 space-y-1'>
                            {linkPreview.title ? (
                              <p className='text-sm font-medium text-foreground line-clamp-2'>{linkPreview.title}</p>
                            ) : null}
                            {linkPreview.description ? (
                              <p className='text-xs text-muted-foreground line-clamp-3'>{linkPreview.description}</p>
                            ) : null}
                            {!linkPreview.title && !linkPreview.description && !linkPreview.image_url ? (
                              <p className='text-xs text-muted-foreground'>{t('adminCoaching.libraryPreviewEmpty')}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )}
                    <Label htmlFor='pc-link-title' className='pt-2'>
                      {t('adminCoaching.libraryTitleOptional')}
                    </Label>
                    <Input
                      id='pc-link-title'
                      value={linkTitle}
                      onChange={(e) => setLinkTitle(e.target.value)}
                    />
                    <Button type='button' className='mt-2 gap-2' disabled={addingLink || !linkUrl.trim()} onClick={submitLink}>
                      {addingLink ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
                      {addingLink ? t('adminCoaching.librarySaving') : t('adminCoaching.libraryAddLink')}
                    </Button>
                  </div>
                  <div className='space-y-2'>
                    <Label>{t('adminCoaching.libraryUpload')}</Label>
                    <Input
                      type='file'
                      accept='.pdf,.docx,.xlsx,.xls,.pptx,.md,.txt'
                      disabled={uploading}
                      onChange={onUploadFile}
                      className='cursor-pointer'
                    />
                    <p className='text-xs text-muted-foreground'>{t('adminCoaching.libraryUploadHint')}</p>
                  </div>
                </div>
                <Separator />
                {platformLoading ? (
                  <div className='flex items-center gap-2 text-muted-foreground text-sm'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    {t('common.loading')}
                  </div>
                ) : platformItems.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('common.noData')}</p>
                ) : (
                  <div className='rounded-md border border-border overflow-x-auto'>
                    <div className='min-w-[780px] grid grid-cols-[52px_minmax(0,1.2fr)_100px_100px_80px] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b bg-muted/40 items-center'>
                      <div className='text-center text-[10px] text-muted-foreground'>{t('adminCoaching.colThumb')}</div>
                      <div>{t('adminCoaching.colTitle')}</div>
                      <div>{t('adminCoaching.colType')}</div>
                      <div>{t('adminCoaching.colCoaching')}</div>
                      <div className='text-right'>{t('adminCoaching.colActions')}</div>
                    </div>
                    {platformItems.map((row) => (
                      <div
                        key={row.id}
                        className='min-w-[780px] grid grid-cols-[52px_minmax(0,1.2fr)_100px_100px_80px] gap-2 px-3 py-2 text-sm border-b border-border/80 last:border-0 items-center'
                      >
                        <div className='shrink-0'>
                          {row.thumbnail_path ? (
                            <img
                              src={row.thumbnail_path}
                              alt=''
                              className='w-12 h-12 rounded-md object-cover bg-muted border border-border'
                            />
                          ) : (
                            <div className='w-12 h-12 rounded-md bg-muted border border-border' aria-hidden />
                          )}
                        </div>
                        <div className='min-w-0'>
                          <p className='font-medium text-foreground truncate' title={row.title}>
                            {row.title}
                          </p>
                          {row.source_url && (
                            <a
                              href={row.source_url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-xs text-primary hover:underline truncate block'
                            >
                              {row.source_url}
                            </a>
                          )}
                        </div>
                        <Badge variant='outline' className='w-fit text-[10px]'>
                          {row.content_type}
                        </Badge>
                        <Switch
                          checked={!!row.is_coaching_skill}
                          onCheckedChange={(c) => toggleCoaching(row, c)}
                          aria-label={t('adminCoaching.colCoaching')}
                        />
                        <div className='flex justify-end'>
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            className='text-destructive hover:text-destructive hover:bg-destructive/10'
                            onClick={() => deletePlatformRow(row.id)}
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='actions' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>{t('adminCoaching.ingestQueueTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.ingestQueueDesc')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='flex flex-wrap items-center gap-2'>
                  <Button
                    type='button'
                    variant='secondary'
                    size='sm'
                    disabled={syncingCoachingQueue}
                    className='gap-2'
                    onClick={syncCoachingQueueFromLibrary}
                  >
                    {syncingCoachingQueue ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
                    {t('adminCoaching.syncCoachingQueue')}
                  </Button>
                  <span className='text-xs text-muted-foreground max-w-xl'>{t('adminCoaching.syncCoachingQueueHint')}</span>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {['open', 'suggested', 'pending', 'executed', 'dismissed', 'all'].map((f) => (
                    <Button
                      key={f}
                      type='button'
                      size='sm'
                      variant={actionFilter === f ? 'default' : 'outline'}
                      onClick={() => {
                        setActionFilter(f);
                        loadActions(f);
                      }}
                    >
                      {t(`adminCoaching.actionFilter.${f}`)}
                    </Button>
                  ))}
                </div>
                {actionLoading ? (
                  <div className='flex items-center gap-2 text-muted-foreground text-sm'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    {t('common.loading')}
                  </div>
                ) : actionItems.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('adminCoaching.noActions')}</p>
                ) : (
                  <ul className='space-y-3'>
                    {actionItems.map((a) => (
                      <li
                        key={a.id}
                        className='rounded-lg border border-border bg-card/50 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'
                      >
                        <div className='min-w-0'>
                          <p className='font-medium text-foreground'>{a.title}</p>
                          <p className='text-xs text-muted-foreground'>
                            {a.action_type} · {a.status} · {a.origin}
                          </p>
                          {a.summary && <p className='text-sm text-muted-foreground mt-1 line-clamp-2'>{a.summary}</p>}
                        </div>
                        <div className='flex flex-wrap gap-2 shrink-0'>
                          {(a.status === 'suggested' || a.status === 'pending') && (
                            <>
                              <Button type='button' size='sm' className='gap-1' onClick={() => executeAction(a.id)}>
                                <Play className='h-3.5 w-3.5' />
                                {t('adminCoaching.executeIngest')}
                              </Button>
                              <Button type='button' size='sm' variant='outline' onClick={() => dismissAction(a.id)}>
                                {t('adminCoaching.dismiss')}
                              </Button>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('adminCoaching.manualActionTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.manualActionDesc')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-3 max-w-[min(90vw,640px)]'>
                <div className='space-y-2'>
                  <Label htmlFor='ma-title'>{t('adminCoaching.manualTitle')}</Label>
                  <Input id='ma-title' value={manualTitle} onChange={(e) => setManualTitle(e.target.value)} />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='ma-slug'>{t('adminCoaching.manualSlug')}</Label>
                  <Input
                    id='ma-slug'
                    placeholder='topic-slug'
                    value={manualSlug}
                    onChange={(e) => setManualSlug(e.target.value)}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='ma-text'>{t('adminCoaching.manualText')}</Label>
                  <textarea
                    id='ma-text'
                    className='flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    value={manualText}
                    onChange={(e) => setManualText(e.target.value)}
                  />
                </div>
                <Button type='button' disabled={manualSubmitting} onClick={submitManualAction}>
                  {manualSubmitting ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
                  {t('adminCoaching.manualSubmit')}
                </Button>
              </CardContent>
            </Card>

            <Card className='border-primary/20 bg-gradient-to-br from-card to-primary/5'>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <RefreshCw className={cn('h-5 w-5 text-primary', reindexing && 'animate-spin')} />
                  {t('adminCoaching.reindexTitle')}
                </CardTitle>
                <CardDescription>{t('adminCoaching.reindexDesc')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <p className='text-sm text-muted-foreground'>{t('adminCoaching.reindexWarning')}</p>
                <Button
                  type='button'
                  disabled={reindexing}
                  className='gap-2'
                  onClick={() => setReindexDialogOpen(true)}
                >
                  {reindexing ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='h-4 w-4' />
                  )}
                  {t('adminCoaching.reindexButton')}
                </Button>
                <Dialog open={reindexDialogOpen} onOpenChange={setReindexDialogOpen}>
                  <DialogContent className='max-w-[min(90vw,520px)]'>
                    <DialogHeader>
                      <DialogTitle>{t('adminCoaching.reindexConfirmTitle')}</DialogTitle>
                      <DialogDescription>{t('adminCoaching.reindexConfirmBody')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className='gap-2 sm:gap-0'>
                      <Button type='button' variant='outline' onClick={() => setReindexDialogOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button type='button' onClick={runReindex}>
                        {t('adminCoaching.reindexButton')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='gap' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>{t('adminCoaching.gapTitle')}</CardTitle>
                <CardDescription>{t('adminCoaching.gapDesc')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <Button type='button' disabled={gapRunning} onClick={runGapAnalysis} className='gap-2'>
                  {gapRunning ? <Loader2 className='h-4 w-4 animate-spin' /> : <Sparkles className='h-4 w-4' />}
                  {t('adminCoaching.gapRun')}
                </Button>
                {gapRun && (
                  <p className='text-sm text-muted-foreground'>
                    {t('adminCoaching.gapRunStatus')}: {gapRun.status}
                  </p>
                )}
                {gapRecs.length > 0 && (
                  <ul className='space-y-3'>
                    {gapRecs.map((r) => (
                      <li key={r.id} className='rounded-lg border border-border p-3 space-y-2'>
                        <div className='flex flex-wrap items-center gap-2'>
                          <span className='font-medium text-foreground'>{r.title}</span>
                          <Badge variant='secondary' className='text-[10px]'>
                            {r.rec_type}
                          </Badge>
                          <Badge variant='outline' className='text-[10px]'>
                            {r.status}
                          </Badge>
                        </div>
                        {r.payload?.rationale && (
                          <p className='text-sm text-muted-foreground'>{r.payload.rationale}</p>
                        )}
                        {r.status === 'draft' && (
                          <div className='flex flex-wrap gap-2'>
                            <Button type='button' size='sm' onClick={() => promoteRec(r.id, 'action')}>
                              {t('adminCoaching.gapPromoteAction')}
                            </Button>
                            <Button type='button' size='sm' variant='outline' onClick={() => promoteRec(r.id, 'platform_link')}>
                              {t('adminCoaching.gapPromoteLink')}
                            </Button>
                            <Button type='button' size='sm' variant='ghost' onClick={() => dismissRec(r.id)}>
                              {t('adminCoaching.dismiss')}
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </AppPageShell>
  );
}

export default function CoachingSkillsAdminPage() {
  return (
    <PermissionProvider>
      <CoachingSkillsAdminContent />
    </PermissionProvider>
  );
}
